import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { describeError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState } from "@/components/common/EmptyState";
import HearingDialog from "@/pages/HearingDialog";
import HearingCompleteDialog from "@/pages/HearingCompleteDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format,
  addMonths, isSameMonth, isSameDay, parseISO, isToday,
} from "date-fns";
import { Plus, ChevronLeft, ChevronRight, CheckCircle2, Pencil, XCircle } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Hearings() {
  const navigate = useNavigate();
  const [hearings, setHearings] = useState(null);
  const [cursor, setCursor] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(new Date());
  const [completeTarget, setCompleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const doCancel = async () => {
    try {
      await api.post(`/hearings/${cancelTarget.id}/cancel`);
      toast.success("Hearing cancelled");
      load();
    } catch (err) { toast.error(describeError(err, "Could not cancel the hearing.")); }
    finally { setCancelTarget(null); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/hearings");
    setHearings(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const byDay = useMemo(() => {
    const m = {};
    (hearings || []).forEach((h) => { (m[h.date] = m[h.date] || []).push(h); });
    return m;
  }, [hearings]);

  if (!hearings) return <LoadingState />;
  const selKey = format(selected, "yyyy-MM-dd");
  const selHearings = (byDay[selKey] || []).sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  return (
    <div data-testid="hearings-page">
      <PageHeader
        title="Court Calendar"
        subtitle="Your hearings across all cases."
        actions={<Button onClick={() => setDialogOpen(true)} data-testid="new-hearing-btn" className="bg-gold text-navy hover:bg-gold-dark"><Plus className="mr-1.5 h-4 w-4" /> New Hearing</Button>}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-border p-4 shadow-sm lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-2xl text-navy">{format(cursor, "MMMM yyyy")}</h2>
            <div className="flex gap-1">
              <button onClick={() => setCursor(addMonths(cursor, -1))} className="flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-secondary" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => setCursor(new Date())} className="rounded-md border border-border px-3 text-sm hover:bg-secondary">Today</button>
              <button onClick={() => setCursor(addMonths(cursor, 1))} className="flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-secondary" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted-foreground">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const key = format(d, "yyyy-MM-dd");
              const items = byDay[key] || [];
              const sel = isSameDay(d, selected);
              return (
                <button
                  key={key}
                  onClick={() => setSelected(d)}
                  className={cn(
                    "flex min-h-[64px] flex-col items-start rounded-md border p-1.5 text-left transition-colors",
                    sel ? "border-navy bg-navy/5" : "border-transparent hover:bg-secondary",
                    !isSameMonth(d, cursor) && "opacity-40"
                  )}
                >
                  <span className={cn("flex h-6 w-6 items-center justify-center rounded-full text-xs", isToday(d) ? "bg-gold font-semibold text-navy" : "text-navy")}>{format(d, "d")}</span>
                  {items.slice(0, 2).map((h) => (
                    <span key={h.id} className="mt-0.5 w-full truncate rounded bg-navy/10 px-1 text-[10px] text-navy">{h.time} {h.case_title}</span>
                  ))}
                  {items.length > 2 && <span className="text-[10px] text-muted-foreground">+{items.length - 2}</span>}
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="border-border shadow-sm">
          <div className="border-b border-border px-5 py-4"><h3 className="font-display text-xl text-navy">{format(selected, "EEEE, dd MMM")}</h3></div>
          <div className="divide-y divide-border">
            {selHearings.length === 0 && <div className="px-5 py-10 text-center text-sm text-muted-foreground">No hearings on this day.</div>}
            {selHearings.map((h) => (
              <div key={h.id} className="px-5 py-3">
                <button onClick={() => navigate(`/cases/${h.case_id}`)} className="w-full text-left" data-testid={`cal-hearing-${h.id}`}>
                  <div className="flex items-center justify-between"><span className="text-sm font-semibold text-navy">{h.time || "—"}</span><StatusBadge status={h.status} /></div>
                  <p className="mt-1 truncate text-sm font-medium text-navy">{h.case_title}</p>
                  <p className="truncate text-xs text-muted-foreground">{h.court} · {h.purpose}</p>
                </button>
                {h.status === "scheduled" && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Button size="sm" onClick={() => setCompleteTarget(h)} data-testid={`cal-complete-${h.id}`} className="h-7 bg-emerald-600 px-2 text-xs text-white hover:bg-emerald-700"><CheckCircle2 className="mr-1 h-3 w-3" />Complete</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditTarget(h)} data-testid={`cal-edit-${h.id}`} className="h-7 px-2 text-xs"><Pencil className="mr-1 h-3 w-3" />Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => setCancelTarget(h)} data-testid={`cal-cancel-${h.id}`} className="h-7 px-2 text-xs text-rose-600 hover:bg-rose-50"><XCircle className="mr-1 h-3 w-3" />Cancel</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <HearingDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />
      <HearingDialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)} caseId={editTarget?.case_id} initial={editTarget} onSaved={load} />
      <HearingCompleteDialog open={!!completeTarget} onOpenChange={(o) => !o && setCompleteTarget(null)} hearing={completeTarget} onSaved={load} />

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this hearing?</AlertDialogTitle>
            <AlertDialogDescription>The hearing will be marked as Cancelled. The record is preserved in the case history.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cal-cancel-dismiss">Keep hearing</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} data-testid="cal-cancel-confirm" className="bg-rose-600 hover:bg-rose-700">Cancel hearing</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
